import type { ComponentPropsOptions } from "vue";
import type { Size } from "@/components/helpers/common-props";
import { defineComponent } from "@vue/runtime-core";
import { useVModel } from "@vueuse/core";
import { NumberFieldDecrement, NumberFieldIncrement, NumberFieldInput, NumberFieldRoot } from "reka-ui";
import { computed } from "vue";
import Icon from "@/components/ui/icon/Icon";
import { inputVariants } from "@/components/ui/input/variants.input";
import { locale } from "@/services/i18n";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";
import { fieldCtx } from "../field/Field";

export type NumberType = "default" | "percentage" | "currency";

const inputNumberProps = {
  modelValue: p.number(),
  id: p.string(),
  name: p.string(),
  min: p.number(0),
  max: p.number(),
  step: p.number(),
  disabled: p.boolean(),
  placeholder: p.string(),
  suffix: p.string(),
  precision: p.number(2),
  type: p.string<NumberType>("default"),
  size: p.string<Size>("md"),
  clearable: p.boolean(false),
  hideButtons: p.boolean(),
  currency: p.string("USD"),
  variant: p.string<"default" | "filled" | "ghost">("default"),
  buttonPlacement: p.string<"right" | "both">("both"),
} satisfies ComponentPropsOptions;

export default defineComponent({
  name: "InputNumber",
  inheritAttrs: false,
  props: inputNumberProps,
  setup(props, { slots, attrs }) {
    const vModel = useVModel(props, "modelValue");
    const field = fieldCtx.inject(props);
    const size = computed(() => props.size ?? field.size ?? "md");
    const intlLocale = computed(() => (locale.value === "pt" ? "pt-BR" : "en-US"));

    const formatOptions = computed<Intl.NumberFormatOptions | undefined>(() => {
      if (props.type === "currency") {
        return {
          style: "currency",
          currency: props.currency ?? "USD",
          minimumFractionDigits: props.precision,
          maximumFractionDigits: props.precision,
        };
      }
      if (props.type === "percentage") {
        return {
          style: "percent",
          minimumFractionDigits: 0,
          maximumFractionDigits: 3,
        };
      }
      return undefined;
    });

    const showButtons = computed(() => !props.hideButtons);
    const placement = computed(() => props.buttonPlacement ?? "both");
    const hasSuffix = computed(
      () => props.suffix || slots.suffix,
    );
    const suffixPosition = computed(() => {
      if (!showButtons.value) return "right-3";
      if (placement.value === "both") return "right-10";
      return "right-6";
    });

    return () => (
      <NumberFieldRoot
        modelValue={vModel.value}
        onUpdate:modelValue={(val: number) => {
          vModel.value = val;
        }}
        min={props.min}
        max={props.max}
        step={props.step}
        formatOptions={formatOptions.value}
        locale={intlLocale.value}
        disabled={props.disabled}
        id={field.id ?? props.id}
        name={props.name}
      >
        <div class={cn("relative w-full", attrs.class as string)}>
          {/* Both placement: Decrement on left */}
          {placement.value === "both" && showButtons.value && (
            <NumberFieldDecrement class="absolute left-0 top-1/2 -translate-y-1/2 p-3 disabled:cursor-not-allowed disabled:opacity-20">
              <Icon icon="lucide:minus" class="size-4" />
            </NumberFieldDecrement>
          )}

          <NumberFieldInput
            class={cn(
              inputVariants({ size: size.value, variant: props.variant }),
              placement.value === "both" && showButtons.value && "text-center px-8",
              !showButtons.value && "text-center",
              placement.value === "right" && showButtons.value && "pr-6",
              hasSuffix.value && "pr-10",
            )}
            {...{ placeholder: props.placeholder ?? "0" }}
          />

          {/* Both placement: Increment on right */}
          {placement.value === "both" && showButtons.value && (
            <NumberFieldIncrement class="absolute right-0 top-1/2 -translate-y-1/2 p-3 disabled:cursor-not-allowed disabled:opacity-20">
              <Icon icon="lucide:plus" class="size-4" />
            </NumberFieldIncrement>
          )}

          {/* Right placement: Stacked chevrons */}
          {placement.value === "right" && showButtons.value && (
            <div class="absolute right-1 top-1/2 -translate-y-1/2 flex flex-col">
              <NumberFieldIncrement class="text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-20">
                <Icon icon="lucide:chevron-up" class="size-3" />
              </NumberFieldIncrement>
              <NumberFieldDecrement class="text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-20">
                <Icon icon="lucide:chevron-down" class="size-3" />
              </NumberFieldDecrement>
            </div>
          )}

          {/* Custom suffix (slot or prop) */}
          {hasSuffix.value && (
            <span
              data-input-number-suffix
              class={cn(
                "pointer-events-none absolute top-1/2 -translate-y-1/2 text-sm text-muted-foreground",
                suffixPosition.value,
              )}
            >
              {slots.suffix?.() ?? props.suffix}
            </span>
          )}

          {/* Clearable X button */}
          {props.clearable && vModel.value != null && (
            <button
              type="button"
              class={cn(
                "absolute top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground",
                showButtons.value && placement.value === "right" ? "right-6"
                  : showButtons.value && placement.value === "both" ? "right-8"
                  : "right-2",
              )}
              onClick={() => {
                vModel.value = undefined;
              }}
            >
              <Icon icon="lucide:x" class="size-3.5" />
            </button>
          )}
        </div>
      </NumberFieldRoot>
    );
  },
});
